import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Input, Pagination, Select, Spin, Table, Tooltip} from 'antd';
import {Button} from '@dabafinance/react-components';
import moment from 'moment';
import {toast} from 'react-toastify';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {DELETE_THEME, GET_THEMES} from './resolvers';
import {
  FEATURE_OPTIONS,
  errorMessage,
  featureLabel,
  isGqlError,
  readMutation,
} from './constants';
import styles from './themes.module.scss';

/**
 * Instrument Themes list.
 *
 * Server search covers name/description; the feature filter is match-any.
 * There is no status field — every theme here is live to mobile users, which
 * the header says outright rather than leaving to be discovered.
 */
export const InstrumentThemes = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [features, setFeatures] = useState([]);
  const [deleteModal, setDeleteModal] = useState({open: false, theme: null});
  // A URL that 404s used to render the browser's broken-image glyph in the row.
  const [brokenIcons, setBrokenIcons] = useState([]);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.InstrumentThemes),
  );

  // Search previously drove the query directly, firing a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const filters = useMemo(
    () => ({
      page,
      limit,
      paginate: true,
      ...(debouncedSearch ? {search: debouncedSearch} : {}),
      ...(features.length ? {features} : {}),
    }),
    [page, limit, debouncedSearch, features],
  );

  const {data, loading, error, refetch} = useQuery(GET_THEMES, {
    variables: {filters},
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const [deleteTheme, {loading: deleting}] = useMutation(DELETE_THEME, {
    errorPolicy: 'all',
  });

  const result = data?.adminGetInstrumentThemes;
  const unionError = isGqlError(result) ? result : null;
  const rows = unionError ? [] : result?.data || [];
  const pagination = unionError ? null : result?.pagination;
  const isForbidden =
    unionError?.statusCode === 401 || unionError?.statusCode === 403;

  const total = pagination?.total || 0;
  const isFiltered = Boolean(debouncedSearch || features.length);

  const countFor = feature =>
    rows.filter(row => row?.features?.includes(feature)).length;

  const resetFilters = () => {
    setSearch('');
    setFeatures([]);
    setPage(1);
  };

  const handleDelete = () => {
    deleteTheme({variables: {id: deleteModal.theme?.id}})
      .then(mutationResult => {
        const {payload, error: failure} = readMutation(
          mutationResult,
          'deleteInstrumentTheme',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        setDeleteModal({open: false, theme: null});
        refetch();
      })
      .catch(mutationError => toast.error(mutationError?.message));
  };

  const columns = [
    {
      title: 'Theme',
      dataIndex: 'name',
      render: (text, record) => (
        <div className={styles.themeCell}>
          {record.icon && !brokenIcons.includes(record.id) ? (
            <img
              src={record.icon}
              alt=""
              className={styles.themeIcon}
              onError={() =>
                setBrokenIcons(previous =>
                  previous.includes(record.id)
                    ? previous
                    : [...previous, record.id],
                )
              }
            />
          ) : (
            <div className={`${styles.themeIcon} ${styles.themeIconFallback}`}>
              {(record.name ?? '?').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className={styles.nameCell}>
            <span className={styles.primaryText}>{record.name}</span>
            {record.description && (
              <Tooltip title={record.description}>
                <span className={styles.mutedText}>{record.description}</span>
              </Tooltip>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Applies to',
      dataIndex: 'features',
      width: 230,
      render: featureList => (
        <div className={styles.chips}>
          {featureList?.length ? (
            featureList.map(feature => (
              <span
                key={feature}
                className={`${styles.chip} ${styles.chipBrand}`}>
                {featureLabel(feature)}
              </span>
            ))
          ) : (
            <span className={styles.cellMuted}>—</span>
          )}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      width: 130,
      render: createdAt =>
        createdAt ? (
          <span className={styles.cellMuted}>
            {moment(createdAt).format('D MMM YYYY')}
          </span>
        ) : (
          <span className={styles.cellMuted}>—</span>
        ),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      width: 140,
      render: updatedAt =>
        updatedAt ? (
          <Tooltip title={moment(updatedAt).format('D MMMM YYYY, HH:mm')}>
            <span className={styles.cellMuted}>
              {moment(updatedAt).fromNow()}
            </span>
          </Tooltip>
        ) : (
          <span className={styles.cellMuted}>—</span>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (text, record) => (
        // Was one "…" dropdown, which hid both actions behind a click.
        <div
          className={styles.rowActions}
          onClick={event => event.stopPropagation()}>
          <Tooltip title={tabOperations?.update ? 'Edit theme' : 'No access'}>
            <button
              type="button"
              className={styles.iconButton}
              disabled={!tabOperations?.update}
              onClick={() =>
                navigate(`/dashboard/instrument-themes/update/${record.id}`)
              }>
              <PencilIcon />
            </button>
          </Tooltip>
          <Tooltip title={tabOperations?.delete ? 'Delete theme' : 'No access'}>
            <button
              type="button"
              className={`${styles.iconButton} ${styles.iconButtonDanger}`}
              disabled={!tabOperations?.delete}
              onClick={() => setDeleteModal({open: true, theme: record})}>
              <TrashIcon />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  if (isForbidden)
    return (
      <div className={styles.container}>
        <Alert
          type="error"
          showIcon
          message="You do not have access to Instrument Themes"
          description={errorMessage(unionError)}
        />
      </div>
    );

  return (
    <div className={styles.container}>
      <div className={styles.pageHead}>
        <div>
          <h1>Instrument themes</h1>
          <p>
            An illustrated preset mobile applies when investors browse. A theme
            goes live as soon as it is saved — there is no draft state.
          </p>
        </div>
        <Button
          label="Create theme"
          disabled={!tabOperations?.create}
          onClick={() => navigate('/dashboard/instrument-themes/new')}
        />
      </div>

      {(unionError || error) && (
        <Alert
          className={styles.alert}
          type="error"
          showIcon
          message={errorMessage(unionError, error?.message)}
          action={<Button label="Retry" type="secondary" onClick={refetch} />}
        />
      )}

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Themes</span>
          <span className={styles.metricValue}>{total}</span>
          <span className={styles.metricHint}>all live to mobile</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Financial instruments</span>
          <span className={styles.metricValue}>
            {countFor('FINANCIAL_INSTRUMENT')}
          </span>
          <span className={styles.metricHint}>on this page</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Collections</span>
          <span className={styles.metricValue}>{countFor('COLLECTION')}</span>
          <span className={styles.metricHint}>on this page</span>
        </div>
      </div>

      <div className={styles.listPanel}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarSearch}>
            <Input
              allowClear
              placeholder="Search name or description"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          <Select
            allowClear
            mode="multiple"
            className={styles.featureFilter}
            placeholder="Any feature"
            value={features}
            options={FEATURE_OPTIONS}
            onChange={next => {
              setFeatures(next);
              setPage(1);
            }}
          />
          {isFiltered && (
            <button
              type="button"
              className={styles.toolbarReset}
              onClick={resetFilters}>
              Clear
            </button>
          )}
          <div className={styles.toolbarSpacer} />
          <span className={styles.toolbarCount}>
            <b>{total}</b> {total === 1 ? 'theme' : 'themes'}
          </span>
        </div>

        {loading && !rows.length ? (
          <div className={styles.centered}>
            <Spin size="large" />
          </div>
        ) : rows.length === 0 ? (
          <div className={styles.empty}>
            <h3>
              {isFiltered ? 'No themes match those filters' : 'No themes yet'}
            </h3>
            <p>
              {isFiltered
                ? 'Try a different search term, or clear the filters to see everything.'
                : 'A theme is an illustrated preset — an icon, a name and a set of filters — that mobile applies when investors browse.'}
            </p>
            {isFiltered ? (
              <Button
                label="Clear filters"
                type="secondary"
                onClick={resetFilters}
              />
            ) : (
              <Button
                label="Create theme"
                disabled={!tabOperations?.create}
                onClick={() => navigate('/dashboard/instrument-themes/new')}
              />
            )}
          </div>
        ) : (
          <>
            <div className={styles.table}>
              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                onRow={record => ({
                  style: {cursor: 'pointer'},
                  onClick: () =>
                    navigate(
                      `/dashboard/instrument-themes/update/${record.id}`,
                    ),
                })}
              />
            </div>
            {total > limit && (
              <div className={styles.tableFoot}>
                <Pagination
                  current={page}
                  pageSize={limit}
                  total={total}
                  showSizeChanger
                  onChange={(nextPage, nextLimit) => {
                    setPage(nextPage);
                    setLimit(nextLimit);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        openModal={deleteModal.open}
        setOpenModal={() => setDeleteModal({open: false, theme: null})}
        title="Delete theme"
        loading={deleting}
        handleOperation={handleDelete}
        confirmText={
          <span>
            Permanently delete <strong>{deleteModal.theme?.name}</strong>? This
            cannot be undone and there is no restore.
          </span>
        }
      />
    </div>
  );
};

const PencilIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default InstrumentThemes;
